import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { SharedService } from 'src/app/core/services/shared.service';
import { RootStore } from 'src/app/store/app.reducer';
import { translations } from 'src/i18n/translations';
import * as InvestmentCompanyActions from 'src/app/modules/investment-companies/store/investment-company.actions';
import * as CompanyActions from 'src/app/modules/companies/store/company.actions';
import { PepCopy } from 'src/app/modules/pep-list/pep-list.interface';
import { createUserManualPep } from 'src/app/modules/users/store/user.actions';
import { clearPepList } from 'src/app/store/pep-check/pep-list.actions';
import { ObjectId } from '../../../../core/interfaces/shared.interface';

declare const UIkit: any;

export const modalId = '#editAnswerModal';

export interface AddManualPepCheckData {
  name: string;
  userId?: string;
}

export enum PepStatus {
  None = 'NONE',
  Pep = 'PEP',
  Sanction = 'SANCTION',
  PepAndSanction = 'PEP_AND_SANCTION'
}

@Component({
  selector: 'app-add-manual-pep-check',
  templateUrl: './add-manual-pep-check.component.html',
  styleUrls: ['./add-manual-pep-check.component.scss']
})
export class AddManualPepCheckComponent implements OnInit {
  pepStatusOptions = [
    { id: PepStatus.None, label: 'None' },
    { id: PepStatus.Pep, label: 'Pep' },
    { id: PepStatus.Sanction, label: 'Sanction' },
    { id: PepStatus.PepAndSanction, label: 'PepAndSanction' }
  ];

  pepCheckForm: FormGroup;

  @Input() name: string;

  @Input() userId: string;

  @Input() companyId: string;
  @Input() type: 'investment-company' | 'company';
  @Input() pepCopy!: PepCopy['copies'];

  @ViewChild('modal', { static: true }) modal: ElementRef;

  constructor(private sharedService: SharedService, private fb: FormBuilder, private store: Store<RootStore>) {}

  ngOnInit(): void {
    UIkit.modal(modalId).show();

    (UIkit.util as Record<string, any>).on(modalId, 'hidden', this.sharedService.deleteModal.bind(this, this.modal));

    // localize pep options
    this.pepStatusOptions = this.pepStatusOptions.map((pepStatus) => ({
      ...pepStatus,
      label: translations[this.sharedService.language][pepStatus.label]
    }));

    this.pepCheckForm = this.fb.group({ pepChecks: this.fb.array([]) });

    if (this.userId) {
      (this.pepCheckForm.get('pepChecks') as FormArray).push(
        this.fb.group({
          comment: [''],
          status: [this.pepStatusOptions[0].id]
        })
      );
    }

    if (this.companyId && this.pepCopy) {
      for (const pepCheck of this.pepCopy) {
        (this.pepCheckForm.get('pepChecks') as FormArray).push(
          this.fb.group({
            pepCheck: [pepCheck.id],
            comment: [pepCheck.comment || ''],
            status: [pepCheck.status]
          })
        );
      }
    }
  }

  get pepChecks() {
    return this.pepCheckForm.controls['pepChecks'] as FormArray;
  }

  createManualPepCheck() {
    if (this.pepCheckForm.valid) {
      const formValue = this.pepCheckForm.value.pepChecks;
      if (this.userId) {
        this.store.dispatch(createUserManualPep({ user_id: this.userId, status: formValue[0].status, comment: formValue[0].comment }));
      }

      if (this.pepCopy) {
        if (this.type === 'investment-company') {
          this.store.dispatch(
            InvestmentCompanyActions.createManualPepCheck({
              investmentCompanyId: this.companyId,
              updateSet: this.filterUnchangedChecks(formValue)
            })
          );
        } else {
          this.store.dispatch(
            CompanyActions.request.createManualPepCheck({
              companyId: this.companyId,
              updateSet: this.filterUnchangedChecks(formValue)
            })
          );
        }
      }

      this.store.dispatch(clearPepList());

      UIkit.modal(modalId).hide();
    }
  }

  filterUnchangedChecks(formValue: { comment: string; status: string; pepCheck: ObjectId }[]) {
    const originalValue = this.pepCopy.map((pepCheck) => ({
      comment: pepCheck.comment,
      status: pepCheck.status,
      pepCheck: pepCheck.id
    }));

    return formValue.filter((formGroup, i) => !compareObjectFields(formGroup, originalValue[i], ['comment', 'status', 'pepCheck']));
  }

  trackByIdentity = (index: number, item: any) => item;
}

function compareObjectFields(objectA: Record<string, any>, objectB: Record<string, any>, compareFields: string[]) {
  return compareFields.every((field) => objectA[field] === objectB[field]);
}
